/**
 * Servicio de dispositivo
 * Sprint 3 - HU-FE-02/03: Bind y gestión de dispositivo vinculado
 */

import { apiClient } from './api';
import {
    getDeviceIdentity,
    getCurrentTimestamp,
    getDevicePlatform,
} from './deviceIdentity';
import {
    generateLoginSignature,
    generateBindSignature,
} from '../utils/crypto';
import { APP_VERSION } from '../config/version';
import type {
    DeviceBindData,
    DeviceBindResponse,
    DeviceInfoResponse,
    LoginWithDeviceCredentials,
    LoginWithDeviceResponse,
} from '../types';

export const deviceService = {
    /**
     * Prepara las credenciales de login con datos del dispositivo
     * HU-FE-02: Enviar datos de dispositivo en login
     */
    async prepareLoginCredentials(
        email: string,
        password: string
    ): Promise<LoginWithDeviceCredentials> {
        const { uuid, secret } = getDeviceIdentity();
        const timestamp = getCurrentTimestamp();
        const signature = await generateLoginSignature(secret, uuid, timestamp);

        return {
            email,
            password,
            device_uuid: uuid,
            device_secret: secret,
            timestamp,
            device_signature: signature,
        };
    },

    /**
     * Login con validación de dispositivo
     * Retorna el resultado o lanza error (incluyendo DEVICE_BIND_REQUIRED)
     */
    async loginWithDevice(
        email: string,
        password: string
    ): Promise<LoginWithDeviceResponse> {
        const credentials = await this.prepareLoginCredentials(email, password);

        console.log('[deviceService.loginWithDevice] Credentials:', {
            email: credentials.email,
            device_uuid: credentials.device_uuid,
            device_secret: credentials.device_secret?.substring(0, 8) + '...',
            timestamp: credentials.timestamp,
            device_signature: credentials.device_signature?.substring(0, 16) + '...',
        });

        return apiClient.post<LoginWithDeviceResponse>(
            '/api/auth/login',
            credentials,
            false
        );
    },

    /**
     * Vincula el dispositivo a la cuenta del usuario
     * HU-FE-03: Flujo de bind requerido
     */
    async bindDevice(
        email: string,
        password: string,
        nonce: string
    ): Promise<DeviceBindResponse> {
        const { uuid, secret } = getDeviceIdentity();
        const timestamp = getCurrentTimestamp();
        const signature = await generateBindSignature(secret, uuid, nonce, timestamp);
        const platform = getDevicePlatform();

        const data: DeviceBindData = {
            email,
            password,
            device_uuid: uuid,
            device_secret: secret,
            nonce,
            timestamp,
            signature,
            device_platform: platform,
            app_version: APP_VERSION,
            // device_model se podría obtener con un plugin de Capacitor
        };

        return apiClient.post<DeviceBindResponse>(
            '/api/device/bind',
            data,
            false
        );
    },

    /**
     * Obtiene información del dispositivo vinculado
     */
    async getDeviceInfo(): Promise<DeviceInfoResponse> {
        return apiClient.get<DeviceInfoResponse>('/api/device', true);
    },
};
